import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// Dictionary of default images for categories
const defaultCategoryImages: { [key: string]: string } = {
  "Men's": "/assets/mens-category.svg",
  "Women's": "/assets/womens-category.svg",
  "Kids": "/assets/kids-category.svg",
  "Accessories": "/assets/accessories-category.svg",
  "default": "/assets/default-category.svg"
};

interface Category {
  id: string;
  name: string;
  description?: string;
  image: string;
}

interface CategoryCardProps {
  category: Category;
  delay: number;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, delay }) => {
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLAnchorElement>(null);
  
  // Determine the image source with fallback
  const imageSrc = imageError 
    ? (defaultCategoryImages[category.name] || defaultCategoryImages.default)
    : category.image;

  // Parallax effect for card contents on mouse move
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isHovered) return;
      
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left; // x position within the element
      const y = e.clientY - rect.top;  // y position within the element
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const deltaX = (x - centerX) / centerX * 5; // Max rotation of 5deg
      const deltaY = (y - centerY) / centerY * 5;
      
      card.style.transform = `perspective(1000px) rotateX(${-deltaY}deg) rotateY(${deltaX}deg) scale3d(1.02, 1.02, 1.02)`;
    };
    
    const handleMouseLeave = () => {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
    };
    
    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isHovered]);

  return (
    <Link 
      ref={cardRef}
      to={`/category/${category.id}`}
      className="block relative overflow-hidden rounded-2xl shadow-xl transition-all duration-500 group bg-secondary/10 backdrop-blur-sm"
      style={{ 
        transitionDelay: `${delay}ms`,
        transformStyle: 'preserve-3d',
        transition: 'transform 0.3s ease-out'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Gradient overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10 transition-opacity duration-500"
        style={{ opacity: isHovered ? 0.9 : 0.7 }}
      ></div>
      
      {/* Image */}
      <div className="relative overflow-hidden aspect-[4/5]">
        <img
          src={imageSrc}
          alt={category.name}
          onError={() => setImageError(true)}
          className="w-full h-full object-cover transition-all duration-700 ease-out will-change-transform"
          style={{ 
            transform: isHovered ? 'scale(1.1)' : 'scale(1)',
            filter: isHovered ? 'brightness(1.1) contrast(1.1)' : 'brightness(1) contrast(1)'
          }}
        />
      </div>
      
      {/* Text overlay */}
      <div 
        className="absolute bottom-0 left-0 right-0 p-6 z-20 text-white transform transition-all duration-500" 
        style={{ 
          transform: isHovered ? 'translateY(-12px)' : 'translateY(0)',
          zIndex: 20 
        }}
      >
        <h3 className="text-2xl font-bold mb-2 tracking-tight flex items-center">
          <span className="relative">
            {category.name}
            <span 
              className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent transition-all duration-500 ease-in-out"
              style={{ width: isHovered ? '100%' : '0%' }}
            ></span>
          </span>
        </h3>
        
        {category.description && (
          <p 
            className="text-sm text-gray-300 mb-3 max-w-xs transition-all duration-500"
            style={{
              opacity: isHovered ? 1 : 0,
              transform: isHovered ? 'translateY(0)' : 'translateY(10px)'
            }}
          >
            {category.description}
          </p>
        )}
        
        <div className="flex items-center space-x-2 mt-4">
          <span 
            className="text-sm font-medium text-accent transition-all duration-300" 
            style={{ 
              letterSpacing: isHovered ? '0.05em' : '0' 
            }}
          >
            SHOP NOW
          </span>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-4 w-4 text-accent transform transition-all duration-300" 
            style={{ transform: isHovered ? 'translateX(6px)' : 'translateX(0)' }}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </div>
      </div>
      
      {/* Corner decorative elements */}
      <div 
        className="absolute top-0 right-0 w-32 h-32 transform translate-x-16 -translate-y-16 bg-accent/30 rounded-full blur-2xl opacity-0 transition-opacity duration-700"
        style={{ opacity: isHovered ? 0.6 : 0 }}
      ></div>
      
      <div 
        className="absolute bottom-0 left-0 w-24 h-24 transform -translate-x-12 translate-y-12 bg-purple-700/20 rounded-full blur-2xl opacity-0 transition-opacity duration-700"
        style={{ opacity: isHovered ? 0.6 : 0 }}
      ></div>
    </Link>
  );
};

const CategorySection: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([
    { id: "mens", name: "Men's", description: "Men's Grails and Sneakers", image: "https://images.unsplash.com/photo-1556906781-9a412961c28c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80" },
    { id: "womens", name: "Women's", description: "Women's Grails and Sneakers", image: "https://images.unsplash.com/photo-1624211813285-e83873b90934?q=80&w=1965&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" },
    { id: "toddlers", name: "Toddlers (TD)", description: "Grails and sneakers for the little ones", image: "https://images.unsplash.com/photo-1632250321290-4bbc6302daaf?q=80&w=2080&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" },
    { id: "grade-school", name: "Grade School (GS)", description: "Sneakers for the grade school generation", image: "https://images.unsplash.com/photo-1619203696548-2a298e92a587?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" },
  ]);
  
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    
    try {
      observer.observe(sectionRef.current);
    } catch (error) {
      console.error("IntersectionObserver error:", error);
      setIsVisible(true);
    }
    
    return () => {
      try {
        observer.disconnect();
      } catch (error) {
        console.error("Error disconnecting observer:", error);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef} 
      className="py-24 relative overflow-hidden bg-gradient-to-b from-black to-background"
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
        {[...Array(20)].map((_, i) => (
          <div 
            key={i}
            className="absolute bg-accent/30 rounded-full"
            style={{
              width: `${Math.random() * 8 + 4}px`,
              height: `${Math.random() * 8 + 4}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              boxShadow: '0 0 20px 2px rgba(149, 76, 233, 0.3)',
              animation: `float ${Math.random() * 10 + 20}s linear infinite`,
              animationDelay: `${Math.random() * 20}s`,
              opacity: Math.random() * 0.5 + 0.3
            }}
          />
        ))}
      </div>
      
      {/* Main decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-64 -left-64 w-96 h-96 bg-accent/10 blur-3xl rounded-full"></div>
        <div className="absolute -bottom-64 -right-64 w-96 h-96 bg-accent/10 blur-3xl rounded-full"></div>
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-purple-900/5 blur-3xl rounded-full"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div 
          className="mb-16 text-center transform transition-all duration-700"
          style={{ 
            transform: isVisible ? 'translateY(0)' : 'translateY(2rem)',
            opacity: isVisible ? 1 : 0 
          }}
        >
          <span className="inline-block text-accent text-sm font-medium tracking-wider mb-2 relative">
            COLLECTIONS
            <span className="absolute -bottom-1 left-1/4 right-1/4 h-px bg-accent/50"></span>
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Shop By Category</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-500 to-accent mx-auto mb-6"></div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {categories.map((category, index) => (
            <div 
              key={category.id}
              className="transform transition-all"
              style={{ 
                transitionDelay: `${index * 100}ms`,
                transitionDuration: '800ms',
                transform: isVisible ? 'translateY(0)' : 'translateY(4rem)',
                opacity: isVisible ? 1 : 0 
              }}
            >
              <CategoryCard category={category} delay={index * 100} />
            </div>
          ))}
        </div>
      </div>
      
      {/* Bottom wave decoration */}
      <div className="absolute bottom-0 left-0 right-0 h-16 overflow-hidden">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" className="absolute bottom-0 w-full h-auto opacity-10">
          <path fill="currentColor" fillOpacity="1" className="text-accent" d="M0,224L60,224C120,224,240,224,360,197.3C480,171,600,117,720,117.3C840,117,960,171,1080,197.3C1200,224,1320,224,1380,224L1440,224L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"></path>
        </svg>
      </div>
    </section>
  );
};

export default CategorySection; 